import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import ControllingClaimsTable from "../../components/tables/ControllingClaimsTable.jsx";
import { useCustomerSearch } from "../../hooks/useCustomerSearch.js";
import UserContext from "@accredo/fe-shared/contexts/UserContext.js";
import { useControllingPermissions } from "../../hooks/permissions/useControllingPermissions.jsx";
import { CustomerSelector } from "../users/components/CustomerSelector.jsx";

const STORAGE_KEY = "controlling_selected_customer";

export default function ControllingIndexPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { user, loading: userLoading } = useContext(UserContext);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const {
    customers,
    loading: searchLoading,
    searchCustomers,
    clearSearch,
  } = useCustomerSearch();

  const { canViewAll, canViewCustomer } = useControllingPermissions();

  const persistCustomer = useCallback((customer) => {
    if (customer) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(customer));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const loadPersistedCustomer = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  useEffect(() => {
    if (canViewAll()) {
      const persisted = loadPersistedCustomer();
      if (persisted) {
        setSelectedCustomer(persisted);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentCustomerId = canViewAll()
    ? (selectedCustomer?.id ?? null)
    : (user?.kunde_id ?? null);

  const canViewCurrentCustomer = currentCustomerId
    ? canViewCustomer(currentCustomerId)
    : false;

  useEffect(() => {
    if (canViewAll()) {
      searchCustomers("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Controlling", path: "/controlling" },
    ]);
  }, [setBreadcrumbs]);

  // console.log("currentCustomerId", currentCustomerId);
  // console.log("canViewCurrentCustomer", canViewCurrentCustomer);
  // console.log(canViewAll());

  if (userLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" gutterBottom>
          Controlling
        </Typography>

        <Typography variant="body1" color="text.secondary">
          Schäden & Zahlungen für einen Zeitraum prüfen
        </Typography>

        {canViewAll() ? (
          <CustomerSelector
            customers={customers}
            selected_customer={selectedCustomer}
            on_customer_change={(event, new_value) => {
              setSelectedCustomer(new_value);
              persistCustomer(new_value);
            }}
            on_customer_input_change={(event, new_input_value) => {
              if (new_input_value.length >= 2) {
                searchCustomers(new_input_value);
              } else {
                clearSearch();
              }
            }}
            search_loading={searchLoading}
            message={
              "Wählen Sie den Kunden aus, für den Sie Schäden & Zahlungen prüfen möchten."
            }
          />
        ) : null}
      </Box>
      {currentCustomerId && canViewCurrentCustomer ? (
        <ControllingClaimsTable customerId={currentCustomerId} />
      ) : null}
    </>
  );
}
